/**
 * Der Rückweg von unseren Kennungen zu den Zahlen des Classic-Servers.
 *
 * `abbildung.ts` baut beim Download aus jeder Classic-Kennung eine eigene: aus der Aufgabe `123`
 * wird `classic:task:123`. Die Zahl reist also mit, und das Zurückschreiben braucht sie wieder —
 * der Server kennt nur `long`, keine Zeichenketten.
 *
 * ⚠️ **Nicht einfach hinter den letzten Doppelpunkt schauen.** Zwei Fallen:
 *
 * 1. **Beziehungen tragen zwei Zahlen** (`classic:examexaminer:<osce>:<examiner>`). Dort läse man
 *    die zweite und hielte sie für die Kennung der Beziehung.
 * 2. **Relay-Kennungen sind UUIDs.** Steht im Classic-Modus eine Relay-Prüfung in der Ablage —
 *    etwa weil der Modus umgestellt wurde —, dürfen ihre Zeilen nicht als Zahlen durchgehen.
 *    Der Server nähme sie an und legte die Bewertung unter einer fremden Aufgabe ab.
 *
 * Deshalb wird die Form vollständig geprüft: bekannte Art, genau eine Zahl, ganze Zahl.
 */

/**
 * Die Arten, die `abbildung.ts` vergibt, und die genau **eine** Zahl tragen.
 *
 * Die Beziehungsarten (`examexaminer`, `examactor`, `examcandidate`) stehen bewusst **nicht**
 * hier: sie tragen zwei Zahlen und sind keine Einzelkennung.
 */
const EINZELARTEN = [
  'osce',
  'part',
  'station',
  'task',
  'question',
  'answer',
  'examiner',
  'actor',
  'candidate',
  'media',
  'phraselist',
  'phrase',
] as const;

const MUSTER = new RegExp('^classic:(?:' + EINZELARTEN.join('|') + '):(\\d+)$');

/**
 * Die Zahl hinter einer Classic-Kennung, oder `null`, wenn es keine ist.
 *
 * `null` heisst immer „nicht zurückschreibbar" und nie „0" — der Aufrufer muss die Zeile
 * liegenlassen und das protokollieren, statt eine erfundene Kennung zu senden.
 */
export function rohkennung(unsere: string | null | undefined): number | null {
  if (!unsere) return null;
  const treffer = MUSTER.exec(unsere);
  if (!treffer) return null;
  const zahl = Number(treffer[1]);
  // `\d+` schliesst Vorzeichen, Punkt und Exponent schon aus; die Prüfung fängt ab, was
  // darüber hinaus keine ganze Zahl mehr ist (sehr lange Ziffernfolgen).
  return Number.isSafeInteger(zahl) ? zahl : null;
}

/**
 * Der Schlüssel, unter dem die Antwortzeile einer **global bewerteten** Aufgabe in
 * `ClassicKennung` steht.
 *
 * Warum es diesen Sonderfall gibt: Relay drückt „global bewertet" dadurch aus, dass es **keine**
 * `ExamAnswer` gibt (`readExam.ts::scoringKindOf`), also wirft `abbildung.ts` die einzige
 * Antwortzeile beim Download weg. Für die Punkte ist das folgenlos — sie gehen ins Aufgabenbewertung.
 * Die Wortmarkierung hängt in Classic aber am `Zeilenbewertung` genau dieser Zeile, und dafür braucht
 * es ihre Kennung. Sie steht deshalb in der Tabelle statt im Namen.
 *
 * Die zurückgegebene Zeichenkette ist **keine** Classic-Kennung; `rohkennung()` löst sie
 * absichtlich nicht auf.
 */
export function kennungGlobalerAntwort(itemId: string): string {
  return 'classic:globalanswer:' + itemId;
}

/**
 * Die Antwortzeilen einer global bewerteten Aufgabe, in der Reihenfolge des Blocks.
 *
 * ⚠️ **Warum das mehr als eine Kennung ist.** Die Wortmarkierung hat auf beiden Seiten ein
 * Ziel, aber verschieden viele: bei uns die **eine** `ExamQuestion` (`SELECTED_WORDS` trägt
 * `EXAM_QUESTION`, s. `persistence/seed.ts`), in Classic das `Zeilenbewertung` **je Antwortzeile**
 * (`answerID` ist dort Pflicht). Zeigt eine Aufgabe mehrere Zeilen als einen Block, laufen
 * unsere Indizes über den ganzen Block, Classics über je eine Zeile — Index 40 an einer Zeile
 * mit zwölf Wörtern wäre dort sinnlos.
 *
 * Aufgeteilt wird deshalb beim Zurückschreiben (`bewertungen.ts`), und dafür braucht es die
 * Wortzahl jeder Zeile. Sie wird beim Download gemerkt, weil nur dort die Zeilen einzeln
 * vorliegen.
 */
export interface GlobaleAntwortzeile {
  /** Die Classic-Kennung der Antwortzeile. */
  id: number;
  /** Wie viele Wörter ihr Text im Block beisteuert. */
  woerter: number;
}

/**
 * Die Zeilen als Zeichenkette für `ClassicKennung.roh`.
 *
 * JSON und nicht eine Kommaliste: die Wortzahl gehört zur Kennung, und zwei parallele Listen
 * liefen irgendwann auseinander.
 */
export function zeilenAlsRoh(zeilen: GlobaleAntwortzeile[]): string {
  return JSON.stringify(zeilen.map((z) => [z.id, z.woerter]));
}

/**
 * Der Rückweg. Versteht **beide** Formen:
 *
 * - `"[[3382,12],[3383,9]]"` — seit dem Mehrzeilen-Fall (2026-09-13)
 * - `"3382"` — eine nackte Zahl, wie sie Prüfungen tragen, die vorher geladen wurden. Ihre
 *   Wortzahl ist unbekannt; sie bekommt `Infinity`, damit **alle** Marken an diese eine Zeile
 *   gehen — genau das Verhalten von vorher.
 */
export function zeilenAusRoh(roh: string | undefined): GlobaleAntwortzeile[] {
  if (!roh) return [];
  if (!roh.trimStart().startsWith('[')) {
    const zahl = Number(roh);
    return Number.isSafeInteger(zahl) ? [{ id: zahl, woerter: Number.POSITIVE_INFINITY }] : [];
  }
  try {
    const gelesen: unknown = JSON.parse(roh);
    if (!Array.isArray(gelesen)) return [];
    return gelesen
      .map((paar) => (Array.isArray(paar) ? { id: Number(paar[0]), woerter: Number(paar[1]) } : null))
      .filter((z): z is GlobaleAntwortzeile => z !== null && Number.isSafeInteger(z.id));
  } catch {
    // Eine unlesbare Notiz ist kein Grund, die Bewertung zu verlieren — sie bleibt liegen.
    return [];
  }
}

/**
 * Block-Indizes auf die Zeilen verteilen, jeweils relativ zum Beginn der Zeile.
 *
 * Beispiel: zwei Zeilen zu 3 und 4 Wörtern, markiert sind `[1, 4]` → Zeile 1 bekommt `[1]`,
 * Zeile 2 bekommt `[1]` (der Block-Index 4 ist dort das zweite Wort).
 *
 * Zeilen ohne Marken kommen **nicht** vor: ein `Zeilenbewertung` ohne Inhalt wäre eine Zeile
 * Rauschen in der Auswertung.
 *
 * ⚠️ **Die Kennung `0` ist keine Zeile, sondern eine Lücke.** Der Download vergibt sie an eine
 * Antwortzeile, die der Server ohne `id` geschickt hat (`abbildung.ts`). Ihre Wörter zählen —
 * sonst verrutschte alles dahinter —, aber ein `Zeilenbewertung` kann es für sie nicht geben:
 * `answerID` ist am Classic-Server Pflicht. Marken auf diesen Wörtern gehen also verloren; das ist
 * die einzige Wahl, die die übrigen Zeilen richtig lässt.
 */
export function verteileWortindexe(
  zeilen: GlobaleAntwortzeile[],
  indexe: number[],
): { id: number; indexe: number[] }[] {
  const sortiert = [...indexe].sort((a, b) => a - b);
  const ergebnis: { id: number; indexe: number[] }[] = [];
  let beginn = 0;
  for (const zeile of zeilen) {
    const ende = beginn + zeile.woerter;
    const treffer = sortiert.filter((i) => i >= beginn && i < ende).map((i) => i - beginn);
    if (treffer.length > 0 && zeile.id > 0) ergebnis.push({ id: zeile.id, indexe: treffer });
    beginn = ende;
  }
  return ergebnis;
}
